import { LOGO_SVG } from '../brand/logo'
import { esc, type Translate } from '../build/content'
import { LOCALES } from '../i18n/locales'
import { localizedPath, stripLocale } from '../i18n/routing'

/**
 * The site's header navigation and footer, as markup strings.
 *
 * One renderer for both surfaces: the Vue apps mount these strings (`SiteNav`,
 * `SiteFooter`), and the static content pages — rendered at build time without
 * Vue — embed them directly. The two cannot drift apart, which hand-kept copies
 * of the same markup always eventually do. Styles are global, in `controls.css`.
 *
 * Pure: imported by Vite configs, which cannot load Vue.
 */

/** Who runs the site. Named in the footer and the legal pages. */
export const OPERATOR = 'HighLevel Codes'

export const CONTACT_EMAIL = 'contact@itbench.tools'

interface SiteLink {
  /** i18n key of the label. */
  readonly key: string
  /** Site-root-relative path, without locale prefix. */
  readonly path: string
}

export const NAV_LINKS: readonly SiteLink[] = [
  { key: 'nav.guides', path: '/guides/' },
  { key: 'nav.faq', path: '/faq/' },
  { key: 'nav.legal', path: '/legal/' },
  { key: 'nav.contact', path: '/contact/' },
]

/**
 * The tools linked from the footer. The homepage's catalogue
 * (`tools/homepage/src/model/tools.ts`) is the source of truth, but the shell
 * cannot import from a tool; a homepage test keeps the two in step.
 */
export const FOOTER_TOOLS: readonly SiteLink[] = [
  { key: 'tools.subnetCalculator', path: '/subnet-calculator/' },
  { key: 'tools.ppiCalculator', path: '/ppi-calculator/' },
]

const FOOTER_COLUMNS: readonly { readonly key: string; readonly links: readonly SiteLink[] }[] = [
  { key: 'footer.tools', links: FOOTER_TOOLS },
  {
    key: 'footer.resources',
    links: [
      { key: 'nav.guides', path: '/guides/' },
      { key: 'nav.faq', path: '/faq/' },
      { key: 'nav.contact', path: '/contact/' },
    ],
  },
  {
    key: 'footer.legal',
    links: [
      { key: 'nav.privacy', path: '/legal/privacy/' },
      { key: 'nav.terms', path: '/legal/terms/' },
    ],
  },
]

/**
 * `aria-current="page"` on the page itself, `"true"` anywhere inside its section,
 * so a guide still highlights Guides.
 */
function current(link: SiteLink, path: string): string {
  const here = stripLocale(path)
  if (here === link.path) return ' aria-current="page"'
  return here.startsWith(link.path) ? ' aria-current="true"' : ''
}

export function renderSiteNav(t: Translate, locale: string, path: string): string {
  const links = NAV_LINKS.map(
    (link) =>
      `<a href="${localizedPath(link.path, locale)}"${current(link, path)}>${esc(t(link.key))}</a>`,
  ).join('')
  return `<nav class="site-nav" aria-label="${esc(t('nav.label'))}">${links}</nav>`
}

export function renderSiteFooter(t: Translate, locale: string, year: number): string {
  const columns = FOOTER_COLUMNS.map((column) => {
    const items = column.links
      .map(
        (link) => `<li><a href="${localizedPath(link.path, locale)}">${esc(t(link.key))}</a></li>`,
      )
      .join('')
    return `<nav class="site-footer__column" aria-label="${esc(t(column.key))}"><h2>${esc(t(column.key))}</h2><ul>${items}</ul></nav>`
  }).join('')

  return `<footer class="site-footer">
  <div class="site-footer__top">
    <div class="site-footer__about">
      <a class="site-logo" href="${localizedPath('/', locale)}">${LOGO_SVG}<span class="visually-hidden">${esc(t('shell.siteName'))}</span></a>
      <p>${esc(t('shell.tagline'))}</p>
      <p class="site-footer__promise">${esc(t('shell.footer.privacy'))}</p>
    </div>
    ${columns}
  </div>
  <div class="site-footer__bottom">
    <span>${esc(t('footer.copyright', { year, operator: OPERATOR }))}</span>
    <a href="mailto:${CONTACT_EMAIL}">${CONTACT_EMAIL}</a>
  </div>
</footer>`
}

/**
 * Header for a static page: logo, navigation, and language and theme switchers
 * emitting the SAME markup and classes as the app's `LanguageSwitcher` and
 * `ThemeSwitcher`.
 *
 * The select needs `staticChromeScript` to navigate, so plain links live in a
 * <noscript> fallback. Either way the authoritative signal for crawlers is the
 * hreflang block in <head>.
 */
export function renderStaticHeader(t: Translate, locale: string, path: string): string {
  const options = LOCALES.map((entry) => {
    const selected = entry.code === locale ? ' selected' : ''
    return `<option value="${localizedPath(path, entry.code)}" data-locale="${entry.code}"${selected}>${entry.flag} ${esc(entry.nativeName)}</option>`
  }).join('')

  const fallback = LOCALES.filter((entry) => entry.code !== locale)
    .map(
      (entry) =>
        `<a href="${localizedPath(path, entry.code)}" hreflang="${entry.hreflang}" lang="${entry.hreflang}">${entry.flag} ${esc(entry.nativeName)}</a>`,
    )
    .join('')

  return `<header class="doc__chrome">
  <div class="doc__brand">
    <a class="site-logo" href="${localizedPath('/', locale)}">${LOGO_SVG}<span class="visually-hidden">${esc(t('shell.siteName'))}</span></a>
  </div>
  ${renderSiteNav(t, locale, path)}
  <div class="switchers">
    <label class="language-switcher">
      <span class="visually-hidden">${esc(t('language.select'))}</span>
      <select id="doc-language" aria-label="${esc(t('language.select'))}">${options}</select>
    </label>
    <button type="button" class="theme-switcher" id="doc-theme">
      <span class="icon" aria-hidden="true"></span>
      <span class="name"></span>
    </button>
  </div>
  <noscript>
    <nav class="doc__langs" aria-label="${esc(t('language.label'))}">${fallback}</nav>
  </noscript>
</header>`
}

/**
 * Drives the static header's switchers. Mirrors the app exactly: the same
 * light -> dark -> system cycle, the same `it-tools:theme` key, and a language
 * change that navigates to the locale's URL.
 */
export function staticChromeScript(t: Translate): string {
  const labels = JSON.stringify({
    light: t('theme.light'),
    dark: t('theme.dark'),
    system: t('theme.system'),
    toggle: t('theme.toggle', { mode: '{mode}' }),
  })
  return `<script>
(function () {
  var KEY = 'it-tools:theme';
  var MODES = ['light', 'dark', 'system'];
  var ICONS = { light: '\\u2600', dark: '\\u263E', system: '\\u25D0' };
  var L = ${labels};

  // Reveal the controls only now: the select cannot navigate without this script,
  // and a dead dropdown beside the <noscript> links is worse than no dropdown.
  var chrome = document.querySelector('.doc__chrome');
  if (chrome) chrome.setAttribute('data-enhanced', '');

  var select = document.getElementById('doc-language');
  if (select) {
    select.addEventListener('change', function () {
      var option = select.options[select.selectedIndex];
      // Persist under the same key the app uses, so a later visit to a bare URL
      // comes up in the language the reader just chose.
      try { localStorage.setItem('it-tools:locale', option.getAttribute('data-locale')); }
      catch (e) {}
      window.location.href = select.value + window.location.hash;
    });
  }

  var btn = document.getElementById('doc-theme');
  if (!btn) return;
  var icon = btn.querySelector('.icon');
  var name = btn.querySelector('.name');
  var query = matchMedia('(prefers-color-scheme: dark)');
  function read() {
    try { var m = localStorage.getItem(KEY); return MODES.indexOf(m) >= 0 ? m : 'system'; }
    catch (e) { return 'system'; }
  }
  function apply(mode) {
    document.documentElement.dataset.theme =
      mode === 'system' ? (query.matches ? 'dark' : 'light') : mode;
    icon.textContent = ICONS[mode];
    name.textContent = L[mode];
    var label = L.toggle.replace('{mode}', L[mode]);
    btn.setAttribute('aria-label', label);
    btn.setAttribute('title', label);
  }
  var mode = read();
  apply(mode);
  btn.addEventListener('click', function () {
    mode = MODES[(MODES.indexOf(mode) + 1) % MODES.length];
    try { localStorage.setItem(KEY, mode); } catch (e) {}
    apply(mode);
  });
  query.addEventListener('change', function () { if (mode === 'system') apply(mode); });
})();
</script>`
}

/**
 * A complete static page body: header, the page's `<main>` content, footer and
 * the switchers' script. `main` is the inner HTML of `<main class="doc">`.
 */
export function renderStaticPage(options: {
  t: Translate
  locale: string
  path: string
  main: string
  year: number
}): string {
  const { t, locale, path, main, year } = options
  return `<div class="doc-shell">
${renderStaticHeader(t, locale, path)}
<main id="main" class="doc">
${main}
</main>
${renderSiteFooter(t, locale, year)}
</div>
${staticChromeScript(t)}`
}
